import type { resolvePrefs } from "./types"
import type { DiagramEdge, DiagramNode, DiagramSpec } from "./types"

export const PASTE_OFFSET = 32
export const SNAP_GRID_SIZE = 16
const AUTO_FORMAT_START_X = 80
const AUTO_FORMAT_START_Y = 80
const AUTO_FORMAT_SECTION_GAP = 80
const AUTO_FORMAT_FREE_GAP = 48
const AUTO_FORMAT_CHILD_GAP = 28
const AUTO_FORMAT_CHILD_PADDING_X = 36
const AUTO_FORMAT_CHILD_PADDING_TOP = 76
const AUTO_FORMAT_CHILD_PADDING_BOTTOM = 36
const AUTO_FORMAT_SECTION_MIN_WIDTH = 340
const AUTO_FORMAT_SECTION_MIN_HEIGHT = 220
const AUTO_FORMAT_FREE_COLUMNS = 3
const AUTO_FORMAT_SECTION_COLUMNS = 2

export interface CanvasPositionNode {
  id: string
  position: { x: number; y: number }
}

export interface FinalDimensionChange {
  type: "dimensions"
  id: string
  dimensions?: { width: number; height: number }
  resizing?: boolean
}

export interface NodeResizeEnd {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export interface EdgeReconnect {
  edgeId: string
  source?: string
  target?: string
}

export interface CopiedSelection {
  nodes: DiagramNode[]
  edges: DiagramEdge[]
}

export function snapValue(value: number, gridSize = SNAP_GRID_SIZE) {
  return Math.round(value / gridSize) * gridSize
}

export function snapPosition(position: { x: number; y: number }, gridSize = SNAP_GRID_SIZE) {
  return {
    x: snapValue(position.x, gridSize),
    y: snapValue(position.y, gridSize),
  }
}

export function makeUniqueId(prefix: string, existingIds: Set<string>) {
  let index = existingIds.size + 1
  let id = `${prefix}-${index}`
  while (existingIds.has(id)) {
    index += 1
    id = `${prefix}-${index}`
  }
  return id
}

export function getNodeBody(node: DiagramNode) {
  return node.description ? `${node.label}\n\n${node.description}` : node.label
}

export function commitNodeBody(node: DiagramNode, body: string): DiagramNode {
  const label = body.trim() || "Untitled"
  if (node.label === label && node.description === undefined) return node
  return { ...node, label, description: undefined }
}

export function applyDiagramMetadataCommit(diagram: DiagramSpec, nameDraft: string, descriptionDraft: string) {
  const name = nameDraft.trim() || "Untitled diagram"
  const description = descriptionDraft.trim() || undefined
  if (diagram.name === name && diagram.description === description) return diagram
  return { ...diagram, name, description }
}

export function applyNodeBodyCommit(diagram: DiagramSpec, nodeId: string, body: string) {
  let changed = false
  const nodes = diagram.nodes.map((node) => {
    if (node.id !== nodeId) return node
    const nextNode = commitNodeBody(node, body)
    if (nextNode !== node) changed = true
    return nextNode
  })

  if (!changed) return diagram
  return { ...diagram, nodes }
}

export function orderNodesForReactFlow(nodes: DiagramNode[]) {
  return [...nodes].sort((left, right) => {
    if (left.isGroup && !right.isGroup) return -1
    if (!left.isGroup && right.isGroup) return 1
    if (left.id === right.parentNode) return -1
    if (right.id === left.parentNode) return 1
    return 0
  })
}

export function getNodeSize(node: DiagramNode, prefs: ReturnType<typeof resolvePrefs>) {
  return {
    width: node.width ?? prefs.nodeWidth,
    height: node.height ?? (node.isGroup ? 300 : 90),
  }
}

function getAbsoluteNodePosition(node: CanvasPositionNode, specNode: DiagramNode, diagram: DiagramSpec) {
  if (!specNode.parentNode) return node.position

  const parent = diagram.nodes.find((candidate) => candidate.id === specNode.parentNode)
  const parentPosition = parent?.position ?? { x: 0, y: 0 }
  return {
    x: parentPosition.x + node.position.x,
    y: parentPosition.y + node.position.y,
  }
}

function findContainingSection(
  node: CanvasPositionNode,
  specNode: DiagramNode,
  diagram: DiagramSpec,
  prefs: ReturnType<typeof resolvePrefs>,
) {
  if (specNode.isGroup) return null

  const absolutePosition = getAbsoluteNodePosition(node, specNode, diagram)
  const nodeSize = getNodeSize(specNode, prefs)
  const nodeCenter = {
    x: absolutePosition.x + nodeSize.width / 2,
    y: absolutePosition.y + nodeSize.height / 2,
  }

  return (
    diagram.nodes.find((candidate) => {
      if (!candidate.isGroup || candidate.id === specNode.id) return false
      const position = candidate.position ?? { x: 0, y: 0 }
      const size = getNodeSize(candidate, prefs)
      return (
        nodeCenter.x >= position.x &&
        nodeCenter.y >= position.y &&
        nodeCenter.x <= position.x + size.width &&
        nodeCenter.y <= position.y + size.height
      )
    }) ?? null
  )
}

export function getDroppedNodePlacement(
  node: CanvasPositionNode,
  specNode: DiagramNode,
  diagram: DiagramSpec,
  prefs: ReturnType<typeof resolvePrefs>,
) {
  if (!specNode.parentNode) {
    const containingSection = findContainingSection(node, specNode, diagram, prefs)
    if (containingSection) {
      const sectionPosition = containingSection.position ?? { x: 0, y: 0 }
      return {
        parentNode: containingSection.id,
        position: {
          x: snapValue(node.position.x - sectionPosition.x),
          y: snapValue(node.position.y - sectionPosition.y),
        },
      }
    }

    return {
      parentNode: specNode.parentNode,
      position: snapPosition(node.position),
    }
  }

  const parent = diagram.nodes.find((candidate) => candidate.id === specNode.parentNode)
  if (!parent) {
    return {
      parentNode: undefined,
      position: snapPosition(node.position),
    }
  }

  const nodeSize = getNodeSize(specNode, prefs)
  const parentSize = getNodeSize(parent, prefs)
  const isOutsideParent =
    node.position.x < 0 ||
    node.position.y < 0 ||
    node.position.x + nodeSize.width > parentSize.width ||
    node.position.y + nodeSize.height > parentSize.height

  if (!isOutsideParent) {
    return {
      parentNode: specNode.parentNode,
      position: snapPosition(node.position),
    }
  }

  const parentPosition = parent.position ?? { x: 0, y: 0 }
  const absolutePosition = {
    x: parentPosition.x + node.position.x,
    y: parentPosition.y + node.position.y,
  }
  const containingSection = findContainingSection({ id: node.id, position: absolutePosition }, { ...specNode, parentNode: undefined }, diagram, prefs)
  if (containingSection) {
    const sectionPosition = containingSection.position ?? { x: 0, y: 0 }
    return {
      parentNode: containingSection.id,
      position: {
        x: snapValue(absolutePosition.x - sectionPosition.x),
        y: snapValue(absolutePosition.y - sectionPosition.y),
      },
    }
  }

  return {
    parentNode: undefined,
    position: {
      x: snapValue(absolutePosition.x),
      y: snapValue(absolutePosition.y),
    },
  }
}

export function applyNodeDrop(
  diagram: DiagramSpec,
  node: CanvasPositionNode,
  prefs: ReturnType<typeof resolvePrefs>,
) {
  const specNode = diagram.nodes.find((candidate) => candidate.id === node.id)
  if (!specNode) return diagram

  if (specNode.isGroup) {
    const sectionPosition = snapPosition(node.position)
    const sectionSize = getNodeSize(specNode, prefs)
    let changed = false
    const nodes = diagram.nodes.map((candidate) => {
      if (candidate.id === specNode.id) {
        const currentPosition = candidate.position ?? { x: 0, y: 0 }
        if (currentPosition.x === sectionPosition.x && currentPosition.y === sectionPosition.y) return candidate

        changed = true
        return { ...candidate, position: sectionPosition }
      }

      if (candidate.isGroup || candidate.parentNode) return candidate

      const candidatePosition = candidate.position ?? { x: 0, y: 0 }
      const candidateSize = getNodeSize(candidate, prefs)
      const candidateCenter = {
        x: candidatePosition.x + candidateSize.width / 2,
        y: candidatePosition.y + candidateSize.height / 2,
      }
      const isInsideSection =
        candidateCenter.x >= sectionPosition.x &&
        candidateCenter.y >= sectionPosition.y &&
        candidateCenter.x <= sectionPosition.x + sectionSize.width &&
        candidateCenter.y <= sectionPosition.y + sectionSize.height

      if (!isInsideSection) return candidate

      changed = true
      return {
        ...candidate,
        parentNode: specNode.id,
        position: {
          x: snapValue(candidatePosition.x - sectionPosition.x),
          y: snapValue(candidatePosition.y - sectionPosition.y),
        },
      }
    })

    if (!changed) return diagram
    return { ...diagram, nodes: orderNodesForReactFlow(nodes) }
  }

  const placement = getDroppedNodePlacement(node, specNode, diagram, prefs)
  const currentPosition = specNode.position ?? { x: 0, y: 0 }
  if (
    specNode.parentNode === placement.parentNode &&
    currentPosition.x === placement.position.x &&
    currentPosition.y === placement.position.y
  ) {
    return diagram
  }

  return {
    ...diagram,
    nodes: diagram.nodes.map((candidate) =>
      candidate.id === node.id
        ? {
            ...candidate,
            parentNode: placement.parentNode,
            position: placement.position,
          }
        : candidate,
    ),
  }
}

export function isFinalDimensionChange(change: unknown): change is FinalDimensionChange {
  if (!change || typeof change !== "object") return false
  const candidate = change as Partial<FinalDimensionChange>
  return (
    candidate.type === "dimensions" &&
    typeof candidate.id === "string" &&
    Boolean(candidate.dimensions) &&
    candidate.resizing !== true
  )
}

export function applyFinalDimensionChanges(diagram: DiagramSpec, changes: FinalDimensionChange[]) {
  const finalChanges = changes.filter(isFinalDimensionChange)
  if (finalChanges.length === 0) return diagram

  let changed = false
  const nodes = diagram.nodes.map((specNode) => {
    const nodeChanges = finalChanges.filter((change) => change.id === specNode.id)
    if (nodeChanges.length === 0) return specNode

    return nodeChanges.reduce<DiagramNode>((updatedNode, change) => {
      if (!change.dimensions) return updatedNode
      const width = Math.round(change.dimensions.width)
      const height = Math.round(change.dimensions.height)
      if (updatedNode.width === width && updatedNode.height === height) return updatedNode

      changed = true
      return {
        ...updatedNode,
        width,
        height,
      }
    }, specNode)
  })

  if (!changed) return diagram
  return {
    ...diagram,
    nodes,
  }
}

export function applyNodeResizeEnd(diagram: DiagramSpec, resize: NodeResizeEnd) {
  let changed = false
  const nodes = diagram.nodes.map((node) => {
    if (node.id !== resize.id) return node

    const position = snapPosition({ x: resize.x, y: resize.y })
    const width = Math.max(snapValue(resize.width), SNAP_GRID_SIZE)
    const height = Math.max(snapValue(resize.height), SNAP_GRID_SIZE)
    const currentPosition = node.position ?? { x: 0, y: 0 }
    const isSame =
      currentPosition.x === position.x &&
      currentPosition.y === position.y &&
      node.width === width &&
      node.height === height
    if (isSame) return node

    changed = true
    return {
      ...node,
      position,
      width,
      height,
    }
  })

  if (!changed) return diagram
  return { ...diagram, nodes }
}

export function resizeSectionToContents(diagram: DiagramSpec, sectionId: string, prefs: ReturnType<typeof resolvePrefs>) {
  const section = diagram.nodes.find((node) => node.id === sectionId && node.isGroup)
  if (!section) return diagram

  const children = diagram.nodes.filter((node) => node.parentNode === sectionId)
  if (children.length === 0) return diagram

  const bounds = children.reduce(
    (acc, child) => {
      const position = child.position ?? { x: 0, y: 0 }
      const size = getNodeSize(child, prefs)
      return {
        minX: Math.min(acc.minX, position.x),
        minY: Math.min(acc.minY, position.y),
        maxX: Math.max(acc.maxX, position.x + size.width),
        maxY: Math.max(acc.maxY, position.y + size.height),
      }
    },
    { minX: Number.POSITIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxX: 0, maxY: 0 },
  )

  const paddingX = 32
  const paddingTop = 72
  const paddingBottom = 32
  const offsetX = Math.min(bounds.minX - paddingX, 0)
  const offsetY = Math.min(bounds.minY - paddingTop, 0)
  const width = Math.max(snapValue(bounds.maxX - offsetX + paddingX), section.width ?? 240, 240)
  const height = Math.max(snapValue(bounds.maxY - offsetY + paddingBottom), section.height ?? 160, 160)

  let changed = false
  const nodes = diagram.nodes.map((node) => {
    if (node.id === sectionId) {
      if (node.width === width && node.height === height) return node
      changed = true
      return { ...node, width, height }
    }

    if (node.parentNode !== sectionId) return node
    const position = node.position ?? { x: paddingX, y: paddingTop }
    const nextPosition = {
      x: snapValue(position.x - offsetX),
      y: snapValue(position.y - offsetY),
    }
    if (position.x === nextPosition.x && position.y === nextPosition.y) return node

    changed = true
    return { ...node, position: nextPosition }
  })

  if (!changed) return diagram
  return { ...diagram, nodes }
}

export function reconnectEdge(diagram: DiagramSpec, reconnect: EdgeReconnect) {
  if (!reconnect.source && !reconnect.target) return diagram
  const nodeIds = new Set(diagram.nodes.map((node) => node.id))
  let changed = false
  const edges = diagram.edges.map((edge) => {
    if (edge.id !== reconnect.edgeId) return edge
    const source = reconnect.source ?? edge.source
    const target = reconnect.target ?? edge.target
    if (!nodeIds.has(source) || !nodeIds.has(target) || source === target) return edge
    if (source === edge.source && target === edge.target) return edge

    changed = true
    return { ...edge, source, target }
  })

  if (!changed) return diagram
  return { ...diagram, edges }
}

export function moveNodeZOrder(diagram: DiagramSpec, nodeId: string, direction: 1 | -1) {
  const target = diagram.nodes.find((node) => node.id === nodeId && !node.isGroup && !node.parentNode)
  if (!target) return diagram
  const freeNodes = diagram.nodes.filter((node) => !node.isGroup && !node.parentNode)
  const zValues = freeNodes.map((node, index) => node.zIndex ?? index)
  const minZ = Math.min(...zValues, 0)
  const maxZ = Math.max(...zValues, 0)
  const currentZ = target.zIndex ?? freeNodes.findIndex((node) => node.id === nodeId)
  const nextZ = direction === 1 ? Math.min(currentZ + 1, maxZ + 1) : Math.max(currentZ - 1, minZ - 1)
  if (nextZ === target.zIndex) return diagram

  return {
    ...diagram,
    nodes: diagram.nodes.map((node) => (node.id === nodeId ? { ...node, zIndex: nextZ } : node)),
  }
}

export function copySelection(diagram: DiagramSpec, selectedNodeIds: string[]): CopiedSelection | null {
  const selectedIds = new Set(selectedNodeIds)
  const nodes = diagram.nodes.filter((node) => selectedIds.has(node.id))
  if (nodes.length === 0) return null

  return {
    nodes,
    edges: diagram.edges.filter((edge) => selectedIds.has(edge.source) && selectedIds.has(edge.target)),
  }
}

export function pasteSelection(diagram: DiagramSpec, selection: CopiedSelection) {
  if (selection.nodes.length === 0) {
    return {
      diagram,
      selection: {
        nodes: [],
        edges: [],
      },
    }
  }

  const existingNodeIds = new Set(diagram.nodes.map((node) => node.id))
  const existingEdgeIds = new Set(diagram.edges.map((edge) => edge.id))
  const idMap = new Map<string, string>()

  const nextNodes = selection.nodes.map((node) => {
    const id = makeUniqueId(node.id, existingNodeIds)
    existingNodeIds.add(id)
    idMap.set(node.id, id)
    const copiedParentId = node.parentNode ? idMap.get(node.parentNode) : undefined

    return {
      ...node,
      id,
      parentNode: copiedParentId ?? node.parentNode,
      position: {
        x: Math.round((node.position?.x ?? 120) + PASTE_OFFSET),
        y: Math.round((node.position?.y ?? 120) + PASTE_OFFSET),
      },
    }
  })

  const nextEdges = selection.edges
    .map((edge) => {
      const source = idMap.get(edge.source)
      const target = idMap.get(edge.target)
      if (!source || !target) return null

      const id = makeUniqueId(edge.id, existingEdgeIds)
      existingEdgeIds.add(id)
      return { ...edge, id, source, target }
    })
    .filter((edge): edge is DiagramEdge => Boolean(edge))

  return {
    diagram: {
      ...diagram,
      nodes: [...diagram.nodes, ...orderNodesForReactFlow(nextNodes)],
      edges: [...diagram.edges, ...nextEdges],
    },
    selection: {
      nodes: nextNodes,
      edges: nextEdges,
    },
  }
}

export function deleteSelection(diagram: DiagramSpec, selectedNodeIds: string[], selectedEdgeId: string | null) {
  if (selectedNodeIds.length === 0 && !selectedEdgeId) return diagram

  const selectedIds = new Set(selectedNodeIds)
  const deletedNodeIds = new Set(
    diagram.nodes
      .filter((node) => selectedIds.has(node.id) || selectedIds.has(node.parentNode ?? ""))
      .map((node) => node.id),
  )
  const deletesEdgeById = selectedEdgeId ? diagram.edges.some((edge) => edge.id === selectedEdgeId) : false
  if (deletedNodeIds.size === 0 && !deletesEdgeById) return diagram

  return {
    ...diagram,
    nodes: diagram.nodes.filter((node) => !deletedNodeIds.has(node.id)),
    edges: diagram.edges.filter(
      (edge) => edge.id !== selectedEdgeId && !deletedNodeIds.has(edge.source) && !deletedNodeIds.has(edge.target),
    ),
  }
}

function getGridPosition(index: number, columns: number, width: number, height: number, gap: number) {
  return {
    x: (index % columns) * (width + gap),
    y: Math.floor(index / columns) * (height + gap),
  }
}

function getGridSize(count: number, columns: number, width: number, height: number, gap: number) {
  if (count === 0) return { width: 0, height: 0 }
  const usedColumns = Math.min(count, columns)
  const rows = Math.ceil(count / columns)
  return {
    width: usedColumns * width + Math.max(usedColumns - 1, 0) * gap,
    height: rows * height + Math.max(rows - 1, 0) * gap,
  }
}

export function autoFormatDiagram(diagram: DiagramSpec, prefs: ReturnType<typeof resolvePrefs>) {
  const sections = diagram.nodes.filter((node) => node.isGroup)
  const childIds = new Set(diagram.nodes.filter((node) => node.parentNode).map((node) => node.id))
  const freeNodes = diagram.nodes.filter((node) => !node.isGroup && !childIds.has(node.id))
  const nextNodesById = new Map<string, DiagramNode>()
  const defaultNodeWidth = prefs.nodeWidth
  const defaultNodeHeight = 90
  let cursorX = AUTO_FORMAT_START_X
  let maxSectionBottom = AUTO_FORMAT_START_Y

  sections.forEach((section) => {
    const children = diagram.nodes.filter((node) => node.parentNode === section.id)
    const childWidth = Math.max(defaultNodeWidth, ...children.map((node) => node.width ?? defaultNodeWidth))
    const childHeight = Math.max(defaultNodeHeight, ...children.map((node) => node.height ?? defaultNodeHeight))
    const childGrid = getGridSize(children.length, AUTO_FORMAT_SECTION_COLUMNS, childWidth, childHeight, AUTO_FORMAT_CHILD_GAP)
    const sectionWidth = Math.max(
      section.width ?? AUTO_FORMAT_SECTION_MIN_WIDTH,
      childGrid.width + AUTO_FORMAT_CHILD_PADDING_X * 2,
      AUTO_FORMAT_SECTION_MIN_WIDTH,
    )
    const sectionHeight = Math.max(
      section.height ?? AUTO_FORMAT_SECTION_MIN_HEIGHT,
      childGrid.height + AUTO_FORMAT_CHILD_PADDING_TOP + AUTO_FORMAT_CHILD_PADDING_BOTTOM,
      AUTO_FORMAT_SECTION_MIN_HEIGHT,
    )

    nextNodesById.set(section.id, {
      ...section,
      width: Math.round(sectionWidth),
      height: Math.round(sectionHeight),
      position: { x: Math.round(cursorX), y: AUTO_FORMAT_START_Y },
    })

    children.forEach((child, index) => {
      const position = getGridPosition(index, AUTO_FORMAT_SECTION_COLUMNS, childWidth, childHeight, AUTO_FORMAT_CHILD_GAP)
      nextNodesById.set(child.id, {
        ...child,
        parentNode: section.id,
        width: child.width ?? defaultNodeWidth,
        height: child.height ?? defaultNodeHeight,
        position: {
          x: Math.round(AUTO_FORMAT_CHILD_PADDING_X + position.x),
          y: Math.round(AUTO_FORMAT_CHILD_PADDING_TOP + position.y),
        },
      })
    })

    cursorX += sectionWidth + AUTO_FORMAT_SECTION_GAP
    maxSectionBottom = Math.max(maxSectionBottom, AUTO_FORMAT_START_Y + sectionHeight)
  })

  const freeStartY = sections.length > 0 ? maxSectionBottom + AUTO_FORMAT_SECTION_GAP : AUTO_FORMAT_START_Y
  freeNodes.forEach((node, index) => {
    const width = node.width ?? defaultNodeWidth
    const height = node.height ?? defaultNodeHeight
    const position = getGridPosition(index, AUTO_FORMAT_FREE_COLUMNS, defaultNodeWidth, defaultNodeHeight, AUTO_FORMAT_FREE_GAP)
    nextNodesById.set(node.id, {
      ...node,
      parentNode: undefined,
      width,
      height,
      position: {
        x: Math.round(AUTO_FORMAT_START_X + position.x),
        y: Math.round(freeStartY + position.y),
      },
    })
  })

  const nodes = diagram.nodes.map((node) => nextNodesById.get(node.id) ?? node)
  return {
    ...diagram,
    nodes: orderNodesForReactFlow(nodes),
  }
}
